import classNames from 'classnames';
import { Tag } from '@carbon/react';
import { Link } from 'components/link';
import styles from './interest-source-pill.module.scss';

interface InterestSourcePillProps {
  /** The interest source type (e.g. 'certified', 'system_promoted'). */
  wiSource: string | undefined;
  /** Workspace id used to build the certified-interest link. */
  nriCertifiedWorkspaceId?: number;
  /** Entity id used to build the system-promoted link. */
  interestEntityId?: number;
}

/**
 * Renders a small "Certified" or "System Promoted" pill, optionally
 * wrapped in a Link.  Renders nothing when `wiSource` is neither value.
 *
 * @param {InterestSourcePillProps} props - The component props.
 * @return {JSX.Element | null} A Tag pill (possibly linked), or null.
 */
export function InterestSourcePill({
  wiSource,
  nriCertifiedWorkspaceId,
  interestEntityId,
}: InterestSourcePillProps) {
  if (wiSource === 'certified') {
    const tag = (
      <Tag
        size="sm"
        type="green"
        className={classNames(styles.pill, {
          [styles.clickable]: nriCertifiedWorkspaceId,
        })}
      >
        Certified
      </Tag>
    );

    if (nriCertifiedWorkspaceId) {
      return (
        <Link
          to={`/interest_certification/workspaces/${nriCertifiedWorkspaceId}`}
          openInNewTab
        >
          {tag}
        </Link>
      );
    }

    return tag;
  }

  if (wiSource === 'system_promoted') {
    const tag = (
      <Tag
        size="sm"
        type="gray"
        className={classNames(styles.pill, {
          [styles.clickable]: interestEntityId,
        })}
      >
        System Promoted
      </Tag>
    );

    if (interestEntityId) {
      return (
        <Link to={`/entities/${interestEntityId}?openIC=true`} openInNewTab>
          {tag}
        </Link>
      );
    }

    return tag;
  }

  return null;
}
